import { c as _c } from "react/compiler-runtime";
import { Box } from '@mui/material';
import { ReactNode } from 'react';
import { BalanceSheetCellPosition, BalanceSheetDensity } from '../../BalanceSheetTable.types';
import { useBalanceSheetHeaderStyles } from './BalanceSheetHeader.styles';
export interface BalanceSheetHeaderProps {
  /**
   * Position of the header within the table row
   */
  cellPosition: BalanceSheetCellPosition;
  /**
   * Density setting affecting padding
   */
  density: BalanceSheetDensity;
  /**
   * Text alignment within the header
   */
  alignment: 'left' | 'right';
  /**
   * Primary header content
   */
  text: ReactNode;
  /**
   * Optional secondary text below the header
   */
  subtext?: string;
  /**
   * Whether this is in a presentation context (grayscale colors)
   */
  isPresentation?: boolean;
  /**
   * Whether start-position content aligns with rows that have an expand icon.
   * Disable this for tables that do not have expandable rows.
   */
  indentStartContent?: boolean;
  /**
   * Optional minimum width for the header.
   * Used with flex layout to constrain how small the header can shrink.
   */
  minWidth?: number;
  /**
   * Optional maximum width for the header.
   * Used with flex layout to constrain how large the header can grow.
   */
  maxWidth?: number;
  /**
   * Whether to render a 4px column gap after this header.
   * Used for summary columns to create visual separation between sections.
   */
  hasColumnGap?: boolean;
  role?: 'columnheader';
}

/**
 * BalanceSheetHeader renders a column header for the balance sheet table with
 * appropriate styling based on position and density.
 *
 * Headers always have a 2px bottom border and bold text.
 * Summary position headers have a gray background.
 */
export function BalanceSheetHeader(t0) {
  const $ = _c(31);
  const {
    cellPosition,
    density,
    alignment,
    text,
    subtext,
    isPresentation: t1,
    indentStartContent: t2,
    minWidth,
    maxWidth,
    hasColumnGap: t3,
    role
  } = t0;
  const isPresentation = t1 === undefined ? false : t1;
  const indentStartContent = t2 === undefined ? true : t2;
  const hasColumnGap = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== alignment || $[1] !== cellPosition || $[2] !== density || $[3] !== indentStartContent || $[4] !== isPresentation || $[5] !== maxWidth || $[6] !== minWidth) {
    t4 = {
      cellPosition,
      density,
      alignment,
      isPresentation,
      indentStartContent,
      minWidth,
      maxWidth
    };
    $[0] = alignment;
    $[1] = cellPosition;
    $[2] = density;
    $[3] = indentStartContent;
    $[4] = isPresentation;
    $[5] = maxWidth;
    $[6] = minWidth;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    classes
  } = useBalanceSheetHeaderStyles(t4);
  let t5;
  if ($[8] !== classes.text || $[9] !== text) {
    t5 = <Box className={classes.text}>{text}</Box>;
    $[8] = classes.text;
    $[9] = text;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== classes.primaryContent || $[12] !== t5) {
    t6 = <Box className={classes.primaryContent}>{t5}</Box>;
    $[11] = classes.primaryContent;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== classes.subtext || $[15] !== subtext) {
    t7 = subtext && <Box component="span" className={classes.subtext}>{subtext}</Box>;
    $[14] = classes.subtext;
    $[15] = subtext;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== classes.content || $[18] !== t6 || $[19] !== t7) {
    t8 = <Box className={classes.content}>{t6}{t7}</Box>;
    $[17] = classes.content;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== classes.root || $[22] !== role || $[23] !== t8) {
    t9 = <Box className={classes.root} role={role}>{t8}</Box>;
    $[21] = classes.root;
    $[22] = role;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== classes.columnGap || $[26] !== hasColumnGap) {
    t10 = hasColumnGap && <Box className={classes.columnGap} />;
    $[25] = classes.columnGap;
    $[26] = hasColumnGap;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[28] = t10;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
}